import React, { useEffect, useState, useRef } from 'react';
import api from '@/api';
import { Funnel } from '@phosphor-icons/react';
import { useCurrency } from '@/hooks/useCurrency';
import AccountMenu from './AccountMenu';

const LiveTicker = ({ events, onRefresh }) => {
  const { formatCompact } = useCurrency();
  const [refreshing, setRefreshing] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ sources: ['portfolio', 'tracker'], signals: ['danger', 'success', 'info', 'neutral'] });
  const filterRef = useRef(null);

  useEffect(() => {
    fetchFilters();
    if (onRefresh) handleRefresh();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilters(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchFilters = async () => {
    try {
      const res = await api.get('/vc/ticker/filters');
      setFilters(res.data);
    } catch (err) { /* use defaults */ }
  };

  const saveFilters = async (newFilters) => {
    setFilters(newFilters);
    try {
      await api.patch('/vc/ticker/filters', newFilters);
      if (onRefresh) onRefresh();
    } catch (err) { /* silent */ }
  };

  const toggleSource = (src) => {
    const newSources = filters.sources.includes(src)
      ? filters.sources.filter(s => s !== src)
      : [...filters.sources, src];
    if (newSources.length === 0) return; // Must have at least one
    saveFilters({ ...filters, sources: newSources });
  };

  const toggleSignal = (sig) => {
    const newSignals = filters.signals.includes(sig)
      ? filters.signals.filter(s => s !== sig)
      : [...filters.signals, sig];
    if (newSignals.length === 0) return;
    saveFilters({ ...filters, signals: newSignals });
  };

  const handleRefresh = async () => {
    if (!onRefresh || refreshing) return;
    setRefreshing(true);
    try { await onRefresh(); } finally { setRefreshing(false); }
  };

  if (!events || events.length === 0) {
    return (
      <div className="h-[36px] bg-surface border-b border-border flex items-center" data-testid="live-ticker">
        <div className="flex-1" />
        <div className="flex items-center gap-1.5 px-3 flex-shrink-0" data-testid="ticker-updates-status">
          <div className="w-2 h-2 bg-text-muted rounded-full" />
          <span className="text-[11px] text-text-muted">No updates</span>
        </div>
        <FilterButton filterRef={filterRef} showFilters={showFilters} setShowFilters={setShowFilters} filters={filters} toggleSource={toggleSource} toggleSignal={toggleSignal} />
        <div className="flex-shrink-0 border-l border-border">
          <AccountMenu role="vc" accentColor="#6366f1" size={36} />
        </div>
      </div>
    );
  }

  const duplicatedEvents = [...events, ...events];

  // Currency-aware events store a raw INR `amount` + a "{amt}" placeholder in the
  // description; render it in the viewer's selected display currency.
  const renderDescription = (event) => {
    if (event.amount != null && typeof event.description === 'string' && event.description.includes('{amt}')) {
      return event.description.replace('{amt}', `${formatCompact(event.amount, 'INR')}${event.amount_suffix || ''}`);
    }
    return event.description;
  };

  const getBadgeColor = (type) => {
    switch (type) {
      case 'success': return 'bg-success/10 text-success border border-success/20';
      case 'danger': return 'bg-danger/10 text-danger border border-danger/20';
      case 'info': return 'bg-info/10 text-info border border-info/20';
      case 'neutral': return 'bg-text-primary/8 text-text-primary border border-border';
      default: return 'bg-info/10 text-info border border-info/20';
    }
  };

  const getDotColor = (type) => {
    switch (type) {
      case 'success': return 'bg-success';
      case 'danger': return 'bg-danger';
      case 'info': return 'bg-info';
      case 'neutral': return 'bg-text-muted';
      default: return 'bg-info';
    }
  };

  const getTextColor = (type) => {
    switch (type) {
      case 'success': return 'text-success';
      case 'danger': return 'text-danger';
      case 'info': return 'text-info';
      case 'neutral': return 'text-text-primary';
      default: return 'text-text-primary';
    }
  };

  return (
    <div className="h-[36px] bg-surface border-b border-border flex items-center" data-testid="live-ticker">
      <div className="flex-1 overflow-hidden pl-4">
        <div className="flex gap-8 animate-marquee hover:pause">
          {duplicatedEvents.map((event, index) => (
            <div key={`${event.id}-${index}`} className="flex items-center gap-2.5 flex-shrink-0" data-testid={`ticker-event-${event.event_type || 'unknown'}`}>
              <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${getDotColor(event.badge_type)}`} />
              <span className={`text-[13px] font-semibold ${getTextColor(event.badge_type)}`}>{event.title}</span>
              <span className="text-[13px] text-text-secondary">{renderDescription(event)}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${getBadgeColor(event.badge_type)}`}>
                {event.badge_label}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center gap-1.5 px-3 flex-shrink-0" data-testid="ticker-updates-status">
        <div className="w-2 h-2 bg-success rounded-full animate-pulse" />
        <span className="text-[11px] text-text-muted">Live</span>
      </div>
      <FilterButton filterRef={filterRef} showFilters={showFilters} setShowFilters={setShowFilters} filters={filters} toggleSource={toggleSource} toggleSignal={toggleSignal} />
      <div className="flex-shrink-0 border-l border-border">
        <AccountMenu role="vc" accentColor="#6366f1" size={36} />
      </div>
    </div>
  );
};

const FilterButton = ({ filterRef, showFilters, setShowFilters, filters, toggleSource, toggleSignal }) => {
  const sourceOptions = [
    { key: 'portfolio', label: 'Portfolio' },
    { key: 'tracker', label: 'Tracker' },
  ];
  const signalOptions = [
    { key: 'danger', label: 'Alerts', dot: 'bg-danger' },
    { key: 'success', label: 'Wins', dot: 'bg-success' },
    { key: 'info', label: 'Updates', dot: 'bg-info' },
    { key: 'neutral', label: 'Activity', dot: 'bg-text-muted' },
  ];

  return (
    <div className="relative flex-shrink-0 border-l border-border" ref={filterRef}>
      <button
        onClick={() => setShowFilters(!showFilters)}
        className="w-[36px] h-[36px] flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
        data-testid="ticker-filter-btn"
        title="Filter ticker"
      >
        <Funnel size={16} weight="bold" />
      </button>

      {showFilters && (
        <div className="absolute right-0 top-[36px] w-[200px] bg-surface border border-border rounded-lg shadow-lg z-50" data-testid="ticker-filter-dropdown">
          <div className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Source</div>
            {sourceOptions.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 py-1.5 cursor-pointer group" data-testid={`filter-source-${opt.key}`}>
                <input
                  type="checkbox"
                  checked={filters.sources.includes(opt.key)}
                  onChange={() => toggleSource(opt.key)}
                  className="w-3.5 h-3.5 rounded border-border text-primary accent-primary"
                />
                <span className="text-xs text-text-primary group-hover:text-primary transition-colors">{opt.label}</span>
              </label>
            ))}
          </div>
          <div className="border-t border-border p-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted mb-2">Signal Type</div>
            {signalOptions.map(opt => (
              <label key={opt.key} className="flex items-center gap-2 py-1.5 cursor-pointer group" data-testid={`filter-signal-${opt.key}`}>
                <input
                  type="checkbox"
                  checked={filters.signals.includes(opt.key)}
                  onChange={() => toggleSignal(opt.key)}
                  className="w-3.5 h-3.5 rounded border-border text-primary accent-primary"
                />
                <div className={`w-2 h-2 rounded-full ${opt.dot}`} />
                <span className="text-xs text-text-primary group-hover:text-primary transition-colors">{opt.label}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveTicker;
